/**
 * ImagePreviewPanel
 *
 * Center panel for Master Image Generation
 * - Large image preview
 * - Image properties (dimensions, format, size, etc.)
 * - Generating overlay during image generation
 * - Generated image view with quick actions
 *
 * @accessibility WCAG 2.1 AA compliant
 */

import { Info, Image as ImageIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { GeneratingPlaceholder } from '../gallery/GeneratingPlaceholder';
import { GeneratedImageActions } from './GeneratedImageActions';
import { MiniGalleryPreview } from './MiniGalleryPreview';
import type { Image } from '@/types';

interface ImagePreviewPanelProps {
  preview: string | null;
  imageInfo?: {
    width?: number;
    height?: number;
    format?: string;
    size?: number;
  } | null;
  /** Zeigt Generierungs-Overlay */
  isGenerating?: boolean;
  /** Startzeitpunkt der Generierung */
  generationStartTime?: Date;
  /** Generiertes Bild (nach erfolgreicher Generierung) */
  generatedImage?: Image | null;
  /** Callback wenn neue Generation gestartet wird */
  onNewGeneration?: () => void;
  /** Callback zum Hinzufügen des Bildes zu einem Produkt */
  onAddToProduct?: () => void;
  /** Callback when a gallery image is clicked */
  onGalleryImageClick?: (imageId: string) => void;
}

export function ImagePreviewPanel({
  preview,
  imageInfo,
  isGenerating = false,
  generationStartTime,
  generatedImage,
  onNewGeneration,
  onAddToProduct,
  onGalleryImageClick,
}: ImagePreviewPanelProps) {
  const { t } = useTranslation();

  const galleryStrip = <MiniGalleryPreview onImageClick={onGalleryImageClick} />;

  // Show generated image view if available
  if (generatedImage && !isGenerating) {
    return (
      <div className="flex flex-col h-full">
        {/* Generated Image with Actions */}
        <div className="flex-1 flex gap-6 p-6 overflow-y-auto">
          {/* Image */}
          <div className="flex-1 flex items-center justify-center bg-neutral-100 dark:bg-neutral-900 rounded-lg overflow-hidden">
            <img
              src={`/image/${generatedImage.id}`}
              alt={generatedImage.prompt || 'Generiertes Bild'}
              className="max-w-full max-h-full object-contain"
            />
          </div>

          {/* Quick Actions */}
          <div className="w-64 flex-shrink-0">
            <div className="sticky top-6">
              <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-4">
                Quick Actions
              </h3>
              <GeneratedImageActions image={generatedImage} onNewGeneration={onNewGeneration} onAddToProduct={onAddToProduct} />
            </div>
          </div>
        </div>
        {galleryStrip}
      </div>
    );
  }

  // Empty state (no preview and no generated image)
  if (!preview) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 flex flex-col items-center justify-center p-6 bg-neutral-50 dark:bg-neutral-900/30">
          <ImageIcon className="h-16 w-16 text-neutral-300 dark:text-neutral-700 mb-4" />
          <p className="text-neutral-500 dark:text-neutral-500 text-center">
            {t('generation.noImageSelected')}
            <br />
            <span className="text-sm">{t('generation.uploadOrSelectImage')}</span>
          </p>
        </div>
        {galleryStrip}
      </div>
    );
  }

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Image Preview */}
        <div className="flex-1 flex items-center justify-center bg-neutral-100 dark:bg-neutral-900 rounded-lg overflow-hidden relative">
          <img
            src={preview}
            alt="Selected image preview"
            className="max-w-full max-h-[480px] object-contain"
          />

          {/* Generating Overlay - Transparent background, original image visible */}
          {isGenerating && (
            <GeneratingPlaceholder
              startTime={generationStartTime}
              overlay={true}
            />
          )}
        </div>

        {/* Image Properties */}
        {imageInfo && (
          <div className="space-y-3 border-t border-neutral-200 dark:border-neutral-800 pt-4">
            <div className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
              <Info className="h-4 w-4" />
              Image Properties
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              {imageInfo.width && imageInfo.height && (
                <div>
                  <span className="text-neutral-500 dark:text-neutral-400">Dimensions:</span>
                  <div className="font-medium text-neutral-900 dark:text-neutral-100">
                    {imageInfo.width} × {imageInfo.height}
                  </div>
                </div>
              )}

              {imageInfo.format && (
                <div>
                  <span className="text-neutral-500 dark:text-neutral-400">Format:</span>
                  <div className="font-medium text-neutral-900 dark:text-neutral-100 uppercase">
                    {imageInfo.format}
                  </div>
                </div>
              )}

              {imageInfo.size && (
                <div>
                  <span className="text-neutral-500 dark:text-neutral-400">File Size:</span>
                  <div className="font-medium text-neutral-900 dark:text-neutral-100">
                    {formatBytes(imageInfo.size)}
                  </div>
                </div>
              )}

              {imageInfo.width && imageInfo.height && (
                <div>
                  <span className="text-neutral-500 dark:text-neutral-400">Aspect Ratio:</span>
                  <div className="font-medium text-neutral-900 dark:text-neutral-100">
                    {getAspectRatio(imageInfo.width, imageInfo.height)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {galleryStrip}
    </div>
  );
}

// Helper: Calculate aspect ratio string
function getAspectRatio(width: number, height: number): string {
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(width, height);
  const w = width / divisor;
  const h = height / divisor;

  // Common ratios
  if (w === 1 && h === 1) return '1:1 (Square)';
  if (w === 16 && h === 9) return '16:9 (Wide)';
  if (w === 4 && h === 3) return '4:3 (Standard)';
  if (w === 3 && h === 2) return '3:2 (Photo)';
  if (w === 21 && h === 9) return '21:9 (Ultrawide)';

  return `${w}:${h}`;
}
